import React from 'react';
import { Outlet, Link, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '@/components/ui/button';
import { LayoutDashboard, Users, UserCog, ShieldCheck, LogOut, Settings, Ticket, BarChart3, Wallet, ScrollText, Receipt, BadgeCheck, ClipboardCheck } from 'lucide-react';
import ChangePasswordDialog from './ChangePasswordDialog';
import NotificationBell from './NotificationBell';

const STAFF_ROLES = ['director', 'admin', 'senior_executive', 'executive', 'finance'];

const ROLE_LABELS = {
  director: 'Director',
  admin: 'Admin',
  senior_executive: 'Senior Executive',
  executive: 'Executive',
  finance: 'Finance',
};

const StaffLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();

  if (!STAFF_ROLES.includes(user?.role)) {
    return <Navigate to="/" />;
  }

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : location.pathname === path || location.pathname.startsWith(path + '/');

  const navItem = (to, label, Icon, testId, exact = false) => (
    <Button
      asChild
      variant={isActive(to, exact) ? 'secondary' : 'ghost'}
      className={isActive(to, exact) ? 'text-indigo-700' : 'text-white hover:bg-white/15'}
      data-testid={testId}
    >
      <Link to={to}>
        <Icon className="w-4 h-4 mr-2" />
        {label}
      </Link>
    </Button>
  );

  const canManageUsers = ['director', 'admin', 'senior_executive'].includes(user?.role);
  const canViewAnalytics = ['director', 'admin', 'senior_executive'].includes(user?.role);
  const canViewFinance = ['director', 'finance'].includes(user?.role);
  const canCoordinate = ['director', 'admin', 'senior_executive', 'executive'].includes(user?.role);
  const canViewPlatform = ['director', 'admin'].includes(user?.role);
  const isDirector = user?.role === 'director';

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-gradient-to-r from-indigo-700 to-violet-700 sticky top-0 z-50 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap items-center justify-between gap-3 py-3">
            <Link to="/staff" className="flex items-center gap-3" data-testid="staff-logo-link">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                <ShieldCheck className="w-6 h-6 text-white" />
              </div>
              <div className="leading-tight">
                <div className="text-xl font-bold text-white">WISE Console</div>
                <div className="text-xs text-indigo-100">{ROLE_LABELS[user?.role]}</div>
              </div>
            </Link>

            <div className="flex flex-1 flex-wrap items-center justify-end gap-2">
              {navItem('/staff', 'Dashboard', LayoutDashboard, 'staff-nav-dashboard', true)}
              {navItem('/staff/students', 'Students', Users, 'staff-nav-students')}
              {canCoordinate && navItem('/staff/sessions', 'Sessions', ClipboardCheck, 'staff-nav-sessions')}
              {navItem('/staff/tickets', 'Tickets', Ticket, 'staff-nav-tickets')}
              {canViewAnalytics && navItem('/staff/analytics', 'Analytics', BarChart3, 'staff-nav-analytics')}
              {canViewFinance && navItem('/staff/payments', 'Payments', BadgeCheck, 'staff-nav-payments')}
              {canViewFinance && navItem('/staff/finance', 'Finance', Wallet, 'staff-nav-finance')}
              {canViewFinance && navItem('/staff/expenses', 'Expenses', Receipt, 'staff-nav-expenses')}
              {canManageUsers && navItem('/staff/users', 'Team', UserCog, 'staff-nav-users')}
              {isDirector && navItem('/staff/audit', 'Audit', ScrollText, 'staff-nav-audit')}
              {canViewPlatform &&
                navItem('/admin', user?.role === 'admin' ? 'Faculty' : 'Platform', Settings, 'staff-nav-platform')}

              <div className="flex items-center gap-2 border-l border-white/20 pl-2 ml-1">
                <span className="text-sm text-white/90 hidden sm:inline" data-testid="staff-user-name">
                  {user?.name}
                </span>
                <NotificationBell />
                <ChangePasswordDialog dark />
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="text-white hover:bg-white/15"
                  data-testid="staff-logout-button"
                  aria-label="Log out"
                  title="Log out"
                >
                  <LogOut className="w-4 h-4" />
                  <span className="ml-1 hidden lg:inline">Logout</span>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      <main className="py-8">
        <Outlet />
      </main>
    </div>
  );
};

export default StaffLayout;
